import { cn } from "@moe/priv/utils";
import { Link } from "@tanstack/react-router";
import { memo, useEffect, useRef } from "react";
import { FaDiscord, FaReddit, FaTwitter } from "react-icons/fa";

const SOCIAL_LINKS = [
  {
    href: "https://discord.gg/CNGAZrahmA",
    Icon: FaDiscord,
    label: "Discord",
    colorClass: "group-hover:text-[#7289DA]"
  },
  {
    href: "https://x.com/animedotgf",
    Icon: FaTwitter,
    label: "Twitter",
    colorClass: "group-hover:text-[#1DA1F2]"
  },
  {
    href: "https://www.reddit.com/r/animegf/",
    Icon: FaReddit,
    label: "Reddit",
    colorClass: "group-hover:text-[#FF4500]"
  }
];

const FOOTER_LINKS = [
  { to: "/tos", label: "Terms of Service" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/legal", label: "Legal" },
  { to: "/guidelines", label: "Community Guidelines" },
  { to: "mailto:hey@anime.gf", label: "Contact Us", isExternal: true }
];

interface SocialLinkProps {
  href: string;
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  colorClass: string;
}

const SocialLink = memo(({ href, Icon, label, colorClass }: SocialLinkProps) => (
  <a
    href={href}
    className="text-tx-tertiary group flex items-center transition-colors"
    target="_blank"
    rel="noopener noreferrer"
  >
    <Icon
      className={`text-tx-primary mr-2 transition-colors duration-300 ease-in-out ${colorClass} sm:text-lg md:text-3xl`}
    />
    <span className={`hidden transition-colors duration-300 ease-in-out ${colorClass} sm:inline`}>{label}</span>
  </a>
));

interface FooterLinkProps {
  to: string;
  label: string;
  isExternal?: boolean;
}

const FooterLink = memo(({ to, label, isExternal }: FooterLinkProps) => {
  if (isExternal) {
    return (
      <a
        href={to}
        className="text-tx-tertiary hover:text-tx-primary transition-colors"
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }
  return (
    <Link to={to} className="text-tx-tertiary hover:text-tx-primary transition-colors">
      {label}
    </Link>
  );
});

interface Props {
  className?: string;
}

export const Footer = memo(({ className }: Props) => {
  const footerRef = useRef(null);

  useEffect(() => {
    const footerNode = footerRef.current;
    if (!footerNode) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("opacity-100");
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(footerNode);
    return () => observer.disconnect();
  }, []);

  return (
    <footer
      ref={footerRef}
      className={cn(`mb-10 mt-10 w-full opacity-0 transition-opacity duration-1000 ease-in-out`, className)}
    >
      <div className="mb-4 flex items-center justify-center space-x-12 text-4xl font-semibold md:text-xl">
        {SOCIAL_LINKS.map((link) => (
          <SocialLink key={link.href} {...link} />
        ))}
      </div>

      <div className="flex flex-col items-center justify-center space-y-2 text-sm sm:flex-row sm:space-x-12 sm:space-y-0 sm:text-base">
        {FOOTER_LINKS.map((link) => (
          <FooterLink key={link.to} {...link} />
        ))}
      </div>
    </footer>
  );
});
